import { useCallback, useEffect, useState } from "react"

import { Stripe } from "@stripe/stripe-js"

import { getStripeConfig, loadStripeWithRetry } from "@fuse/utils/stripeUtils"

export interface UseStripeWithRetryResult {
  stripe: Stripe | null
  isLoading: boolean
  error: Error | null
  isLoaded: boolean
  retry: () => void
  attempts: number
}

export const useStripeWithRetry = (): UseStripeWithRetryResult => {
  const [stripe, setStripe] = useState<Stripe | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [attempts, setAttempts] = useState(0)

  const loadStripe = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)

      const config = getStripeConfig()
      const result = await loadStripeWithRetry(config)

      setStripe(result?.stripe)
      setError(result?.error)
      setAttempts(result?.attempts)

      if (result?.error) {
        console.error("Stripe loading failed after retries:", result?.error)
      }
    } catch (configError) {
      const error =
        configError instanceof Error
          ? configError
          : new Error("Configuration error")
      setError(error)
      console.error("Stripe configuration error:", error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const retry = useCallback(() => {
    loadStripe()
  }, [loadStripe])

  useEffect(() => {
    loadStripe()
  }, [loadStripe])

  return {
    stripe,
    isLoading,
    error,
    isLoaded: !isLoading && !error && !!stripe,
    retry,
    attempts,
  }
}
